// src/components/toastService.js
//
// TOAST-T1: Lightweight imperative toast service + ToastHost component.
//
// Usage:
//   import { showToast } from "../components/toastService.js";
//   showToast("Changes saved", { type: "success" });
//   showToast("Please review", { type: "info", action: { label: "View", onClick: () => {} } });
//
// Mount <ToastHost /> once in the app root (App.jsx). No props required.

import { useEffect, useState } from "react";
import { ToastBanner } from "./ToastBanner.jsx";

// ─── Module-level event bus ───────────────────────────────────────────────────

let _nextId = 1;
const _listeners = new Set();

function _emit(toast) {
  for (const fn of _listeners) fn(toast);
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Fire a toast from anywhere in the app without prop-drilling.
 *
 * @param {string} message
 * @param {{ type?: "success"|"info"|"error", action?: { label: string, onClick: () => void } }} [opts]
 */
export function showToast(message, { type = "info", action, duration } = {}) {
  _emit({ id: _nextId++, message, type, action, duration });
}

// ─── ToastHost ────────────────────────────────────────────────────────────────

const MAX_TOASTS    = 2;
const TOAST_GAP     = 12;  // px between stacked toasts
const TOAST_HEIGHT  = 48;  // 22px icon + 24px padding + 2px border
const BOTTOM_BASE   = 24;  // px from bottom for the lowest toast

/**
 * Render once in the app root. Manages the live stack of active toasts.
 * At most 2 toasts are visible; a 3rd push replaces the oldest.
 * Newest toast is on top (highest offsetBottom).
 */
export function ToastHost() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    function handleToast(toast) {
      setToasts((prev) => {
        const next = [...prev, toast];
        // Drop the oldest if we exceed MAX_TOASTS.
        return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
      });
    }
    _listeners.add(handleToast);
    return () => _listeners.delete(handleToast);
  }, []);

  function dismiss(id) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  if (toasts.length === 0) return null;

  // Stack: index 0 is oldest (rendered lowest), last index is newest (on top).
  return (
    <>
      {toasts.map((toast, i) => {
        const offset = BOTTOM_BASE + i * (TOAST_HEIGHT + TOAST_GAP);
        return (
          <ToastBanner
            key={toast.id}
            testId={`toast-host-${toast.id}`}
            message={toast.message}
            type={toast.type ?? "info"}
            action={toast.action}
            duration={toast.duration}
            onDismiss={() => dismiss(toast.id)}
            offsetBottom={offset}
          />
        );
      })}
    </>
  );
}
